import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { MerchantService } from "../../services/Merchant.service";
import { handleApiError } from "../../utils/handleApiError";
import { useI18n } from "../../i18n";
import AdsSection from "../ads/AdsSection";
import { toast } from "react-toastify";

const formatDiscount = (r) =>
  r.discount_type === "percent"
    ? `${parseFloat(r.value)}%`
    : `${parseFloat(r.value)} AZN`;

// Ünvan sahəsinə koordinat yazılıbsa mətn kimi göstərməyə dəyməz — xəritə onsuz da var
const isCoordinates = (value) =>
  typeof value === "string" &&
  /^\s*-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?\s*$/.test(value);

const StorePage = () => {
  const { id } = useParams();
  const { t, tr } = useI18n();
  const [merchant, setMerchant] = useState(null);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mükafat mətnini seçilmiş dildə göstər
  const rewardLabel = (quiz) => {
    if (quiz.reward_mode === "tiered" && quiz.reward_tiers?.length) {
      const max = quiz.reward_tiers[quiz.reward_tiers.length - 1];
      return t("store.reward_tiered", {
        count: max.min_correct,
        reward: formatDiscount(max),
      });
    }
    if (quiz.flat_reward) {
      return t("store.reward_flat", { reward: formatDiscount(quiz.flat_reward) });
    }
    return t("store.reward_default");
  };

  useEffect(() => {
    (async () => {
      try {
        const data = await MerchantService.show(id);
        setMerchant(data.merchant);
        setQuizzes(data.quizzes || []);
      } catch (error) {
        toast.error(handleApiError(error, t));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!merchant) {
    return (
      <div className="text-center text-white py-20 page">
        <p className="text-5xl mb-4">😢</p>
        <p className="text-xl font-semibold">{t("store.not_found")}</p>
        <Link to="/stores" className="underline mt-2 inline-block">
          {t("store.back_to_stores")}
        </Link>
      </div>
    );
  }

  const addressText =
    merchant.address && !isCoordinates(merchant.address)
      ? tr(merchant.address)
      : null;

  return (
    <div className="max-w-4xl mx-auto page pt-6 text-white">
      {/* Başlıq — mağazanın öz banner şəkli arxa fon kimi */}
      <div className="relative overflow-hidden rounded-3xl shadow-2xl border border-white/15 animate-fade-up">
        {merchant.banner && (
          <>
            <img
              src={merchant.banner}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Mətn oxunaqlı qalsın deyə tündləşdirici qat */}
            <div className="absolute inset-0 bg-gradient-to-t from-indigo-950/90 via-purple-900/70 to-purple-900/30" />
          </>
        )}

        <div
          className={`relative p-5 sm:p-8 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 ${
            merchant.banner ? "pt-16 sm:pt-8" : "bg-white/10 backdrop-blur-xl"
          }`}
        >
          {merchant.photo ? (
            <img
              src={merchant.photo}
              alt={tr(merchant.name)}
              className="w-20 h-20 sm:w-28 sm:h-28 object-cover rounded-2xl shadow-lg shrink-0 ring-2 ring-white/40"
            />
          ) : (
            <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-2xl bg-gradient-to-br from-indigo-400 to-fuchsia-500 flex items-center justify-center shadow-lg shrink-0">
              <span className="text-4xl sm:text-5xl font-extrabold">
                {merchant.name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
          )}

          <div className="text-center sm:text-left min-w-0">
            <h1 className="text-2xl sm:text-3xl font-extrabold drop-shadow break-words">
              {tr(merchant.name)}
            </h1>
            {addressText && (
              <p className="text-indigo-200 text-xs sm:text-sm mt-1">
                📍 {addressText}
              </p>
            )}
            {merchant.bio && (
              <div
                className="text-indigo-100 mt-2 text-sm max-w-xl line-clamp-3"
                dangerouslySetInnerHTML={{ __html: tr(merchant.bio) }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Kampaniyalar */}
      <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-4">
        {t("store.active_campaigns")}
      </h2>

      {quizzes.length === 0 ? (
        <div className="glass-card rounded-2xl p-8 text-center">
          <p className="text-4xl mb-3">🎯</p>
          <p className="text-base sm:text-lg">{t("store.no_quiz")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          {quizzes.map((quiz) => (
            <div
              key={quiz.id}
              className="bg-white rounded-2xl p-5 sm:p-6 shadow-xl flex flex-col justify-between hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
            >
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800">
                  {tr(quiz.title)}
                </h3>
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className="text-xs font-semibold bg-indigo-50 text-indigo-600 px-3 py-1 rounded-full">
                    {t("store.questions_count", { count: quiz.total_questions })}
                  </span>
                  {quiz.time_per_question_sec ? (
                    <span className="text-xs font-semibold bg-amber-50 text-amber-600 px-3 py-1 rounded-full">
                      {t("store.per_question", { sec: quiz.time_per_question_sec })}
                    </span>
                  ) : null}
                  <span className="text-xs font-semibold bg-green-50 text-green-600 px-3 py-1 rounded-full">
                    🎁 {rewardLabel(quiz)}
                  </span>
                </div>
              </div>

              <Link
                to={`/play/${merchant.id}/${quiz.id}`}
                className="mt-5 block text-center bg-gradient-to-r from-indigo-500 to-fuchsia-600 text-white font-semibold py-3 rounded-xl hover:opacity-90 active:scale-[0.98] transition"
              >
                {t("store.play_button")}
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* Bu mağazanın reklamları */}
      <AdsSection merchantId={merchant.id} variant="dark" />

      {/* Ünvan / Xəritə */}
      {merchant.latitude && merchant.longitude && (
        <div className="mt-10">
          <h2 className="text-xl sm:text-2xl font-bold mb-4">
            {t("store.address_title")}
          </h2>
          <div className="glass-card rounded-3xl p-4 sm:p-5">
            {addressText && <p className="text-indigo-100 mb-3">{addressText}</p>}
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <iframe
                title={`${tr(merchant.name)} - ${t("store.map_title")}`}
                width="100%"
                height="240"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                src={`https://www.google.com/maps?q=${merchant.latitude},${merchant.longitude}&output=embed`}
              />
            </div>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${merchant.latitude},${merchant.longitude}`}
              target="_blank"
              rel="noreferrer"
              className="inline-block mt-3 text-sm font-semibold text-indigo-200 underline underline-offset-2 hover:text-white transition"
            >
              {t("store.open_in_maps")}
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default StorePage;
